import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import styles from './TopContributorsLeaderboard.module.css';
import LevelBadge from './LevelBadge/LevelBadge';
import { getLevel } from '../utils/levels';
import { Trophy, TrendingUp, Medal, Award } from 'lucide-react';

interface TopContributor {
  userId: number;
  username: string;
  pointsThisWeek: number;
  totalPoints: number;
  currentLevel: number;
  rankChange: number; // +1, -1, 0, or null for new
}

interface TopContributorsLeaderboardProps {
  timeframe?: 'week' | 'month' | 'alltime';
  limit?: number;
  showTitle?: boolean;
}

const TopContributorsLeaderboard: React.FC<TopContributorsLeaderboardProps> = ({
  timeframe = 'week',
  limit = 10,
  showTitle = true
}) => {
  const [contributors, setContributors] = useState<TopContributor[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState(timeframe);

  useEffect(() => {
    fetchLeaderboard();
  }, [selectedTimeframe, limit]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/auth/user/org-points/leaderboard?timeframe=${selectedTimeframe}&limit=${limit}`);
      setContributors(res.data || []);
    } catch (err) {
      console.error('Failed to fetch leaderboard', err);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Trophy size={24} className={styles.goldMedal} />;
      case 2:
        return <Medal size={24} className={styles.silverMedal} />;
      case 3:
        return <Medal size={24} className={styles.bronzeMedal} />;
      default:
        return <div className={styles.rankNumber}>{rank}</div>;
    }
  };

  const getRankChangeIndicator = (change: number | null) => {
    if (change === null) return <span className={styles.newBadge}>NEW</span>;
    if (change > 0) return <span className={styles.rankUp}>▲ {change}</span>;
    if (change < 0) return <span className={styles.rankDown}>▼ {Math.abs(change)}</span>;
    return <span className={styles.rankSame}>─</span>;
  };

  const getTimeframeLabel = () => {
    switch (selectedTimeframe) {
      case 'week':
        return 'This Week';
      case 'month':
        return 'This Month';
      case 'alltime':
        return 'All Time';
      default:
        return 'This Week';
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        {showTitle && <h2 className={styles.title}>🏆 Top Contributors</h2>}
        <div className={styles.loading}>Loading leaderboard...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {showTitle && (
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Trophy size={28} className={styles.headerIcon} />
            <div>
              <h2 className={styles.title}>Top Contributors</h2>
              <p className={styles.subtitle}>Celebrating our most active members</p>
            </div>
          </div>
        </div>
      )}

      <div className={styles.timeframeSelector}>
        <button
          className={selectedTimeframe === 'week' ? styles.timeframeActive : styles.timeframeButton}
          onClick={() => setSelectedTimeframe('week')}
        >
          This Week
        </button>
        <button
          className={selectedTimeframe === 'month' ? styles.timeframeActive : styles.timeframeButton}
          onClick={() => setSelectedTimeframe('month')}
        >
          This Month
        </button>
        <button
          className={selectedTimeframe === 'alltime' ? styles.timeframeActive : styles.timeframeButton}
          onClick={() => setSelectedTimeframe('alltime')}
        >
          All Time
        </button>
      </div>

      <div className={styles.leaderboard}>
        {contributors.length === 0 ? (
          <div className={styles.empty}>No contributors yet for {getTimeframeLabel().toLowerCase()}</div>
        ) : (
          contributors.map((contributor, index) => {
            const rank = index + 1;
            const isTopThree = rank <= 3;

            return (
              <div
                key={contributor.userId}
                className={`${styles.contributorCard} ${isTopThree ? styles.topThree : ''} ${rank === 1 ? styles.firstPlace : ''} ${rank === 2 ? styles.secondPlace : ''} ${rank === 3 ? styles.thirdPlace : ''}`}
              >
                <div className={styles.rankSection}>
                  {getRankIcon(rank)}
                  {getRankChangeIndicator(contributor.rankChange)}
                </div>

                <div className={styles.contributorInfo}>
                  <div className={styles.contributorName}>
                    <span className={styles.username}>
                      {contributor.username}
                      <span className={styles.levelIndicator}> Lv.{contributor.currentLevel}</span>
                    </span>
                  </div>
                  <div className={styles.contributorStats}>
                    {selectedTimeframe !== 'alltime' && (
                      <span className={styles.periodPoints}>
                        <TrendingUp size={14} />
                        {contributor.pointsThisWeek} pts {selectedTimeframe === 'week' ? 'this week' : 'this month'}
                      </span>
                    )}
                    <span className={styles.totalPoints}>
                      {contributor.totalPoints.toLocaleString()} total
                    </span>
                  </div>
                </div>

                <div className={styles.pointsBadge}>
                  {selectedTimeframe === 'alltime' 
                    ? `${contributor.totalPoints.toLocaleString()}`
                    : `+${contributor.pointsThisWeek}`
                  }
                </div>
              </div>
            );
          })
        )}
      </div>

      {contributors.length > 0 && (
        <div className={styles.footer}>
          <Award size={16} />
          <span>Keep contributing to climb the rankings!</span>
        </div>
      )}
    </div>
  );
};

export default TopContributorsLeaderboard;
